import React, { FC, useEffect } from 'react';

import { Icon } from '@iconify/react';
import { IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import Button from '~/components/atoms/buttons/Button';
import { useIsViewport, Viewports } from '~/hooks/useIsViewport';
import analytics from '~/services/analytics';
import { Typographies } from '~/theme/typography';

import styles from './styles.module.scss';
import GiftBoxesDesktopImg from '../../assets/gift_boxes_modal_desktop.webp';
import GiftBoxesMobileImg from '../../assets/gift_boxes_modal_mobile.webp';
import { ReactComponent as MagicIcon } from '../../assets/magic.svg';

type Props = {
  onSubmit: () => Promise<void>;
  onRefuse: () => void;
  onShow: () => void;
};

const BlackFridayModal: FC<Props> = ({ onSubmit, onRefuse, onShow }) => {
  const { t } = useTranslation();
  const isDesktop = useIsViewport(Viewports.EXTRA_SMALL_DESKTOP);

  useEffect(() => {
    analytics.trackEvent('black friday - promo modal show');
    onShow();
  }, []);

  return (
    <div className={styles.content}>
      <img
        className={styles.image}
        src={isDesktop ? GiftBoxesDesktopImg : GiftBoxesMobileImg}
        alt=""
      />

      <div className={styles.close_icon}>
        <IconButton onClick={onRefuse} size="small">
          <Icon icon="material-symbols:close" width="24" color="#76777A" />
        </IconButton>
      </div>

      <div className={styles.header}>
        <Typography
          variant={Typographies.BODY_MEDIUM}
          className={styles.header__title}
          component="div"
        >
          {t('black_friday:dates', '24 November – 7 December')}
        </Typography>
      </div>

      <div className={styles.main}>
        <div className={styles.main__title}>
          {t('black_friday:title', 'Black Friday Discount!')}
        </div>

        <div className={styles.main__plan}>
          <Icon
            icon="material-symbols:all-inclusive"
            className={styles.main__plan_icon}
          />

          <Typography
            variant={
              isDesktop ? Typographies.TITLE_LARGE : Typographies.TITLE_MEDIUM
            }
            className={styles.main__plan_title}
            component="div"
          >
            {t('black_friday:plan_title', 'Unlimited Annual Plan')}
          </Typography>

          <Typography
            variant={Typographies.LABEL_LARGE}
            className={styles.main__plan_discount}
          >
            {t('black_friday:discount_badge', '85% Off')}
          </Typography>
        </div>

        <div className={styles.main__text}>
          {t(
            'black_friday:description',
            'Save $400 and get unlimited words in AI Text Humanizer, Plagiarism Checker, and AI Content Detector',
          )}
        </div>

        <div className={styles.main__price}>
          <MagicIcon className={styles.main__price_icon} />

          <Typography
            variant={Typographies.TITLE_LARGE}
            className={styles.main__price_text}
            component="div"
          >
            {t('black_friday:price', 'Only $79.99')}
          </Typography>

          <MagicIcon className={styles.main__price_icon} />
        </div>
      </div>

      <div className={styles.footer}>
        <Button
          className={styles.footer__submit_button}
          variant="contained"
          color="secondary"
          onClick={onSubmit}
        >
          {t('black_friday:cta_claim', 'Claim my discount')}
        </Button>

        <Button
          className={styles.footer__refuse_button}
          variant="text"
          color="primary"
          onClick={onRefuse}
        >
          {t('black_friday:cta_dismiss', 'Dismiss')}
        </Button>
      </div>
    </div>
  );
};

export default BlackFridayModal;
